import {
  Accordion,
  Box,
  Flex,
  Hide,
  SimpleGrid,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Button, Grid } from '@time-line/components/common';
import { PromoCode } from 'components/common/PromoCode';
import { ProductBadges } from 'components/product/ProductBadges';
import { ProductDisclaimer } from 'components/product/ProductDisclaimer';
import { ProductGalleryDesktop } from 'components/product/ProductGallery/ProductGalleryDesktop';
import { ProductGalleryMobile } from 'components/product/ProductGallery/ProductGalleryMobile';
import { useProductBundle } from 'hooks/product/useProductBundle';
import { useAnalytics } from 'hooks/useAnalytics';
import type { ProductBundle as ProductBundleProps } from 'types/cms/blocks';

import { BundleLineItem } from './BundleLineItem';
import { CtaButton } from './CtaButton';
import { Pricing } from './Pricing';

export function ProductBundle({
  title,
  isMediaDisplayRight,
  description,
  media,
  ctaType,
  isPromoCodeVisible,
  promoCodes,
  products,
  _key,
  productBullet,
  secondaryProductBullet,
}: ProductBundleProps) {
  const { analytics } = useAnalytics();
  const { data: dummyCart } = useProductBundle({
    _key,
    products,
    promoCodes,
  });

  const disclaimers = Array.from(
    new Set(
      products
        .map(({ product: p }) => p.disclaimer)
        .filter((d): d is string => Boolean(d)),
    ),
  );

  const hasDiscount = dummyCart
    ? dummyCart?.totalPrice.amount < dummyCart?.subTotal.amount
    : false;

  const availableForSale = dummyCart?.lines.every(
    (item) => item.merchandise.availableForSale,
  );

  if (promoCodes && promoCodes.length > 0) {
    analytics.viewPromotion({
      code: promoCodes[0].code,
      product: { name: title },
    });
  } else {
    analytics.viewItemList({
      listId: 'featured',
      products: products.map((p) => p.product.slug),
    });
  }

  return (
    <Grid className="section--product-bundle" my="2xl" id={_key}>
      {media && media.length > 0 && (
        <Hide above="lg">
          <Box
            className="image--main"
            gridColumn="span 12"
            display={{ base: 'block', lg: 'none' }}
          >
            <ProductGalleryMobile media={media} title={title} />
          </Box>
        </Hide>
      )}
      <Grid.Inner
        gridRowGap={{ base: 'sm', md: 'lg' }}
        sx={{ direction: isMediaDisplayRight ? 'rtl' : 'ltr' }}
      >
        {media && media.length > 0 && (
          <Hide below="lg">
            <Box
              className="image--main"
              gridColumn="span 8"
              me="5%"
              display={{ base: 'none', lg: 'block' }}
            >
              <ProductGalleryDesktop media={media} title={title} />
            </Box>
          </Hide>
        )}
        <Box
          gridColumn={{ base: '1/-1', lg: '9/-1' }}
          sx={{ direction: 'ltr' }}
          gridRow={{ base: 2, lg: 1 }}
          position="sticky"
          top="2xl"
          alignSelf="start"
        >
          <ProductBadges
            productBullet={productBullet}
            secondaryProductBullet={secondaryProductBullet}
            mb="3xs"
          />
          <Stack alignItems="flex-start" spacing="sm">
            <Stack gap={{ base: '3xs', md: '2xs' }}>
              <Text as="h2" size="h2">
                {title}
              </Text>
              {description && <Text size="body-serif">{description}</Text>}
            </Stack>

            <Flex flexDir="column" gap="3xs" width="100%">
              <Text size="detail" fontWeight="medium">
                What's included
              </Text>
              <Accordion allowToggle>
                {dummyCart?.lines
                  .slice()
                  .reverse()
                  .map((l, i, lines) => (
                    <BundleLineItem
                      key={l.id}
                      line={l}
                      index={i}
                      isLast={i === lines.length - 1}
                    />
                  ))}
              </Accordion>
            </Flex>
            {isPromoCodeVisible &&
              availableForSale &&
              promoCodes &&
              promoCodes.length > 0 && (
                <PromoCode promoCode={promoCodes[0]} title={title} />
              )}
            <Stack width="100%" spacing="xs">
              {dummyCart && (
                <SimpleGrid
                  columns={1}
                  width="100%"
                  columnGap="2xs"
                  rowGap="2xs"
                >
                  {availableForSale ? (
                    <>
                      <Pricing
                        cost={dummyCart.cost}
                        totalPrice={dummyCart.totalPrice}
                        subTotal={dummyCart.subTotal}
                        hasDiscount={hasDiscount}
                      />
                      <CtaButton
                        ctaType={ctaType}
                        products={products}
                        discountCodes={
                          promoCodes ? [promoCodes[0].code] : undefined
                        }
                        size="md"
                        colorScheme="gray"
                      />
                    </>
                  ) : (
                    <Button isDisabled>Out of stock</Button>
                  )}
                </SimpleGrid>
              )}
              {disclaimers && disclaimers.length > 0 && (
                <Stack gap="5xs">
                  {disclaimers.map((disclaimer, i) => (
                    <ProductDisclaimer key={`disclaimer-${i}`}>
                      {disclaimer}
                    </ProductDisclaimer>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>
      </Grid.Inner>
    </Grid>
  );
}
